import { useCallback, useEffect, useState } from 'react';
import { api, clearToken, setToken } from '../api/client.js';

function consumeTokenFromHash() {
  const hash = window.location.hash;
  if (!hash || !hash.startsWith('#')) return false;
  const params = new URLSearchParams(hash.slice(1));
  const token = params.get('token');
  if (!token) return false;
  setToken(token);
  window.history.replaceState(null, '', window.location.pathname + window.location.search);
  return true;
}

function consumeAuthErrorFromQuery() {
  const params = new URLSearchParams(window.location.search);
  const err = params.get('auth_error');
  if (!err) return null;
  params.delete('auth_error');
  const newSearch = params.toString();
  window.history.replaceState(
    null,
    '',
    window.location.pathname + (newSearch ? `?${newSearch}` : ''),
  );
  return err;
}

export function useAuth() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  // Recharge l'utilisateur depuis /auth/me (status/role à jour côté BDD).
  // Lot C — on tente TOUJOURS /auth/me : la session normale passe par le cookie
  // SSO core (pas de token local en localStorage). Le token local legacy
  // (`hg_token`), s'il existe, est envoyé en Bearer par le client.
  const refresh = useCallback(async () => {
    try {
      const { user } = await api.auth.me();
      setUser(user);
      return user;
    } catch {
      clearToken();
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    const errFromQuery = consumeAuthErrorFromQuery();
    if (errFromQuery) setAuthError(errFromQuery);

    consumeTokenFromHash();

    refresh().finally(() => setLoading(false));
  }, [refresh]);

  // Bascule automatique : tant que le compte est PENDING, on interroge /auth/me
  // périodiquement. Dès que l'admin l'active, `user.status` passe à ACTIVE et
  // l'application s'affiche — sans reconnexion ni rechargement manuel.
  useEffect(() => {
    if (user?.status !== 'PENDING') return undefined;
    const id = setInterval(() => { refresh(); }, 10000);
    return () => clearInterval(id);
  }, [user?.status, refresh]);

  // Lot C — redirige vers le SSO core (auth centralisée + cookie cross-subdomain).
  const login = useCallback((next = '/') => {
    window.location.href = api.auth.loginUrl(next);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.auth.logout();
    } catch {
      // token déjà invalide — on continue
    }
    clearToken();
    setUser(null);
  }, []);

  return { user, loading, authError, login, logout, refresh, isAuthenticated: Boolean(user) };
}
